"use client";

import React, { InputHTMLAttributes } from "react";
import { cn } from "./cn";

interface FormInputProps extends InputHTMLAttributes<HTMLInputElement> {
    label: string;
    error?: string;
    icon?: React.ReactNode;
}

export const FormInput = React.forwardRef<HTMLInputElement, FormInputProps>(
    ({ label, error, icon, className, ...props }, ref) => {
        const iconElement = React.isValidElement<{ className?: string }>(icon)
            ? React.cloneElement(icon, {
                  className: cn("h-4 w-4", icon.props.className),
              })
            : icon;

        return (
            <div className="space-y-2 w-full">
                <label className="block text-xs font-bold text-muted-foreground uppercase tracking-widest pl-1">
                    {label}
                    {props.required && <span className="text-destructive ml-1">*</span>}
                </label>
                <div className="relative group">
                    {icon && (
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors pointer-events-none group-focus-within:text-primary">
                            {iconElement}
                        </div>
                    )}
                    <input
                        ref={ref}
                        className={cn(
                            "block w-full py-3 border rounded-xl text-base sm:text-sm font-medium outline-none transition-all bg-muted/20",
                            icon ? "pl-12 pr-4" : "px-4",
                            error
                                ? "border-destructive/30 focus:ring-4 focus:ring-destructive/5 focus:border-destructive/60"
                                : "border-border/60 focus:ring-4 focus:ring-primary/5 focus:border-primary/40 focus:bg-background",
                            className
                        )}
                        {...props}
                    />
                </div>
                {error && <p className="text-[10px] font-bold text-destructive mt-1 uppercase tracking-wider pl-1">{error}</p>}
            </div>
        );
    }
);

FormInput.displayName = "FormInput";
